import { useState, useEffect, useMemo } from "react";
import { UserCog, Shield, LogIn, Search, Edit2, Trash2, Plus } from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import StaffForm, { StaffFormData } from "./StaffForm";
import { useToast } from "@/context/ToastContext";
import { authApi, usersApi } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { withAuth } from "@/app/components/withAuth";
import { formatMonthYear } from "@/lib/dateFormat";

interface StaffMember {
  id: number;
  name: string;
  email: string;
  role: string;
  status: string;
  phone?: string;
  avatar_color?: string;
  join_date?: string;
  created_at?: string;
}

const ROLE_LABELS: Record<string, string> = {
  super_admin:  "Super Admin",
  admin:        "Admin",
  manager:      "Manager",
  receptionist: "Receptionist",
  staff:        "Staff",
};

const ROLE_COLORS: Record<string, string> = {
  super_admin:  "bg-amber-100 text-amber-800 border-amber-300",
  admin:        "bg-amber-50 text-amber-700 border-amber-200",
  manager:      "bg-violet-100 text-violet-700 border-violet-200",
  receptionist: "bg-sky-100 text-sky-700 border-sky-200",
  staff:        "bg-stone-100 text-stone-700 border-stone-200",
};

const AVATAR_GRADIENTS = [
  "from-amber-400 to-orange-500",
  "from-violet-400 to-indigo-500",
  "from-rose-400 to-pink-500",
  "from-emerald-400 to-teal-500",
  "from-sky-400 to-blue-500",
  "from-purple-400 to-fuchsia-500",
];

const ADMIN_ROLES = ["admin", "super_admin"];

const normalizeRole = (raw?: string): string => {
  if (!raw) return "staff";
  const r = raw.toString().trim().toLowerCase().replace(/\s+/g, "_");
  // Map any legacy values to current snake_case keys
  if (r === "master") return "super_admin";
  if (r === "super-admin") return "super_admin";
  return r;
};

function StaffPage() {
  const { showToast } = useToast();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<StaffMember | null>(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [impersonating, setImpersonating] = useState<number | null>(null);
  const [confirmLoginAs, setConfirmLoginAs] = useState<StaffMember | null>(null);

  const isAdmin = ADMIN_ROLES.includes(normalizeRole(currentUser?.role));
  const isSuperAdmin = normalizeRole(currentUser?.role) === "super_admin";

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response: any = await usersApi.list();
      const list: StaffMember[] = response.data?.data || response.data || [];
      const cleaned = list.map(u => ({ ...u, role: normalizeRole(u.role) }));
      setUsers(cleaned);
    } catch (error: unknown) {
      showToast(error instanceof Error ? error.message : "Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (!q) return true;
      return (
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone?.toLowerCase().includes(q)
      );
    });
  }, [users, search, roleFilter]);

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter(u => u.status === "active").length,
    admins: users.filter(u => ADMIN_ROLES.includes(u.role)).length,
    managers: users.filter(u => u.role === "manager").length,
  }), [users]);

  const handleDelete = async (u: StaffMember) => {
    if (u.id === currentUser?.id) {
      showToast("You cannot delete your own account", "error");
      return;
    }
    if (!confirm(`Remove ${u.name}? This cannot be undone.`)) return;
    try {
      await usersApi.delete(u.id);
      showToast("User removed", "success");
      setUsers(prev => prev.filter(x => x.id !== u.id));
    } catch (error: unknown) {
      showToast(error instanceof Error ? error.message : "Failed to remove user", "error");
    }
  };

  const handleSubmit = async (data: StaffFormData) => {
    try {
      const payload = { ...data, role: normalizeRole(data.role) };
      if (editing) {
        await usersApi.update(editing.id, payload);
        showToast("User updated", "success");
      } else {
        await usersApi.create(payload);
        showToast("User created", "success");
      }
      setIsModalOpen(false);
      setEditing(null);
      await loadUsers();
    } catch (error: unknown) {
      showToast(error instanceof Error ? error.message : "Save failed", "error");
    }
  };

  const handleLoginAs = async () => {
    if (!confirmLoginAs) return;
    try {
      setImpersonating(confirmLoginAs.id);
      const response: any = await authApi.impersonate(confirmLoginAs.id);
      const token = response.data?.token || response.data?.data?.token;
      if (token) {
        const url = `${window.location.origin}/impersonate?token=${encodeURIComponent(token)}&name=${encodeURIComponent(confirmLoginAs.name)}`;
        window.open(url, "_blank");
        showToast(`Opened ${confirmLoginAs.name}'s session in new tab`, "success");
      } else {
        showToast("Server did not return an impersonation token", "error");
      }
    } catch (error: unknown) {
      showToast(error instanceof Error ? error.message : "Impersonation failed", "error");
    } finally {
      setImpersonating(null);
      setConfirmLoginAs(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-stone-800">User Management</h1>
            <p className="text-sm text-stone-500 mt-1">
              Create, edit, and manage user accounts. Page-level access is controlled in
              Settings &rarr; Roles &amp; Access.
            </p>
          </div>
          <button
            onClick={() => { setEditing(null); setIsModalOpen(true); }}
            className="text-sm font-semibold text-white px-4 py-2 rounded-xl gold-gradient shadow-sm flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add User
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-stone-200 p-3">
            <div className="text-xs text-stone-500">Total</div>
            <div className="text-2xl font-bold text-stone-800">{stats.total}</div>
          </div>
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-3">
            <div className="text-xs text-emerald-600">Active</div>
            <div className="text-2xl font-bold text-emerald-700">{stats.active}</div>
          </div>
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-3">
            <div className="text-xs text-amber-600">Admins</div>
            <div className="text-2xl font-bold text-amber-700">{stats.admins}</div>
          </div>
          <div className="bg-violet-50 rounded-xl border border-violet-200 p-3">
            <div className="text-xs text-violet-600">Managers</div>
            <div className="text-2xl font-bold text-violet-700">{stats.managers}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-stone-200 p-3 mb-4 flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or phone..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-stone-200 text-sm focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-stone-200 text-sm focus:outline-none focus:border-amber-400"
        >
          <option value="all">All roles</option>
          {Object.entries(ROLE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden mb-6">
        <table className="w-full">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600">User</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600">Role</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600">Email</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600">Phone</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600">Joined</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-stone-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-8 text-sm text-stone-400">
                  No users match your filters.
                </td>
              </tr>
            )}
            {filtered.map((u) => {
              const grad = AVATAR_GRADIENTS[u.id % AVATAR_GRADIENTS.length];
              const isSelf = u.id === currentUser?.id;
              const isOtherAdmin = ADMIN_ROLES.includes(u.role) && !isSelf;
              const isProtectedSuperAdmin = u.role === "super_admin" && !isSuperAdmin;
              if (isProtectedSuperAdmin) return null;
              return (
                <tr key={u.id} className="hover:bg-stone-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold bg-gradient-to-br ${grad}`}>
                        {u.name?.charAt(0).toUpperCase() || "?"}
                      </div>
                      <div>
                        <p className="font-medium text-stone-800 text-sm">
                          {u.name}{isSelf && <span className="text-xs text-amber-600 ml-1">(you)</span>}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${ROLE_COLORS[u.role] || "bg-stone-100 text-stone-700 border-stone-200"}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-600">{u.email}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{u.phone || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                      u.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"
                    }`}>
                      {u.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-stone-500">{formatMonthYear(u.join_date || u.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {isAdmin && !isSelf && !isOtherAdmin && (
                        <button
                          onClick={() => setConfirmLoginAs(u)}
                          className="p-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          title={`Login as ${u.name}`}
                        >
                          <LogIn className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => { setEditing(u); setIsModalOpen(true); }}
                        className="p-2 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(u)}
                        disabled={isSelf}
                        className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={isSelf ? "Cannot delete yourself" : "Delete"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden grid grid-cols-1 gap-3 mb-6">
        {filtered.map((u) => {
          const grad = AVATAR_GRADIENTS[u.id % AVATAR_GRADIENTS.length];
          const isSelf = u.id === currentUser?.id;
          const isOtherAdmin = ADMIN_ROLES.includes(u.role) && !isSelf;
          if (u.role === "super_admin" && !isSuperAdmin) return null;
          return (
            <div key={u.id} className={`bg-white rounded-2xl border border-stone-200 shadow-sm p-4 ${u.status === "inactive" ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white text-lg font-bold bg-gradient-to-br ${grad}`}>
                    {u.name?.charAt(0).toUpperCase() || "?"}
                  </div>
                  <div>
                    <p className="font-bold text-stone-800 text-sm">{u.name}{isSelf && <span className="text-xs text-amber-600 ml-1">(you)</span>}</p>
                    <p className="text-xs text-stone-500">{u.email}</p>
                  </div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${ROLE_COLORS[u.role] || "bg-stone-100 text-stone-700 border-stone-200"}`}>
                  {ROLE_LABELS[u.role] || u.role}
                </span>
              </div>
              <div className="flex gap-2">
                {isAdmin && !isSelf && !isOtherAdmin && (
                  <button
                    onClick={() => setConfirmLoginAs(u)}
                    className="flex-1 text-xs font-semibold py-2 rounded-xl bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center justify-center gap-1"
                  >
                    <LogIn className="w-3.5 h-3.5" />
                    Login as
                  </button>
                )}
                <button
                  onClick={() => { setEditing(u); setIsModalOpen(true); }}
                  className="flex-1 text-xs font-semibold py-2 rounded-xl bg-stone-100 text-stone-600 hover:bg-stone-200"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(u)}
                  disabled={isSelf}
                  className="flex-1 text-xs font-semibold py-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-30"
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add/Edit modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditing(null); }}
        title={editing ? `Edit User: ${editing.name}` : "Add New User"}
        size="lg"
      >
        <StaffForm
          initialData={editing ? {
            name: editing.name,
            email: editing.email,
            role: editing.role,
            status: editing.status,
            phone: editing.phone,
          } : undefined}
          canAssignSuperAdmin={isSuperAdmin}
          onSubmit={handleSubmit}
          onCancel={() => { setIsModalOpen(false); setEditing(null); }}
        />
      </Modal>

      {/* Login-as confirm */}
      <Modal
        isOpen={!!confirmLoginAs}
        onClose={() => setConfirmLoginAs(null)}
        title="Confirm impersonation"
        size="md"
      >
        {confirmLoginAs && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl">
              <Shield className="w-6 h-6 text-emerald-600" />
              <div>
                <p className="font-semibold text-stone-800">{confirmLoginAs.name}</p>
                <p className="text-xs text-stone-500">{confirmLoginAs.email} • {ROLE_LABELS[confirmLoginAs.role]}</p>
              </div>
            </div>
            <p className="text-sm text-stone-600">
              A new tab will open logged in as <strong>{confirmLoginAs.name}</strong>.
              Actions there are recorded as that user.
            </p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setConfirmLoginAs(null)}
                className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200"
              >
                Cancel
              </button>
              <button
                onClick={handleLoginAs}
                disabled={impersonating !== null}
                className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-500 text-white font-semibold text-sm hover:bg-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <LogIn className="w-4 h-4" />
                {impersonating ? "Opening..." : "Open in new tab"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
}

export default withAuth(StaffPage, { allowedRoles: ["admin", "super_admin"] });
